import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import {
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';

interface KeyboardShortcutsPluginProps {
  /**
   * Whether Cmd/Ctrl+U applies underline. Markdown has no underline syntax, so
   * a markdown-backed editor should disable it: otherwise the user applies
   * underline, sees it render, and it silently vanishes on save. Defaults to
   * true so rich-text (HTML) editors keep the shortcut.
   */
  allowUnderline?: boolean;
}

export function KeyboardShortcutsPlugin({
  allowUnderline = true,
}: KeyboardShortcutsPluginProps = {}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const { metaKey, ctrlKey, key } = event;

        // Check for cmd/ctrl key combinations
        if (metaKey || ctrlKey) {
          switch (key.toLowerCase()) {
            case 'b':
              event.preventDefault();
              event.stopPropagation();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
              return true;

            case 'i':
              event.preventDefault();
              event.stopPropagation();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
              return true;

            case 'u':
              // Always consume Cmd/Ctrl+U — when underline is disabled we still
              // swallow it so the browser's native underline can't sneak in.
              event.preventDefault();
              event.stopPropagation();
              if (allowUnderline) {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
              }
              return true;

            // Add more shortcuts as needed
            default:
              break;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, allowUnderline]);

  return null;
}
